import { DateRangePicker as MuiDateRangePicker, SingleInputDateRangeField, usePickerContext } from '@mui/x-date-pickers-pro';
import { DateRangePickerProps } from '@mui/x-date-pickers-pro';
import { Dayjs } from 'dayjs';
import { AppInputCustomProps, CustomFieldProps, mobileShortcutsItems, shortcutsItems } from './types';
import { IconButton, InputAdornment, Theme, useMediaQuery } from '@mui/material';
import FormLabel from './FormLabel';
import classNames from 'classnames/bind';
import { FocusEventHandler, useMemo } from 'react';
import CalendarMonthIcon from '@mui/icons-material/EventTwoTone';
import CustomRangeShortcuts from './DateRangeShortcuts';
import ClearInputAdornment from './ClearInputAdornment';
import ClockIcon from '@mui/icons-material/QueryBuilderTwoTone';

import styles from './Form.module.css';
const styleContext = classNames.bind(styles);

export type DatePickerRangeValue = [Dayjs | null, Dayjs | null];

/**
 *
 */
export default function DateRangePicker({ customProps, ...componentProps }: DateRangePickerProps & AppInputCustomProps) {
  const rangePickerClasses = styleContext({
    input: true,
    dirty: customProps.isDirty,
    error: customProps.isError,
    disabled: componentProps.disabled
  });

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
    <MuiDateRangePicker
      {...componentProps}
      className={`${componentProps.className ?? ''} ${rangePickerClasses}`}
      label={
        <FormLabel
          text={customProps.labelText}
          error={customProps.isError}
          errorText={customProps.errorText}
          required={customProps.required}
        />
      }
      disabled={componentProps.disabled}
      calendars={isMobile ? 1 : 2}
      slots={{
        field: SingleInputDateRangeField,
        shortcuts: CustomRangeShortcuts
      }}
      slotProps={{
        shortcuts: {
          items: isMobile ? mobileShortcutsItems : shortcutsItems
        },
        toolbar: {
          hidden: isMobile
        },
        actionBar: {
          hidden: isMobile
        },
        textField: {
          variant: customProps.variant,
          onBlur: customProps.onBlur as FocusEventHandler<HTMLDivElement>,
          error: customProps.isError,
          slotProps: {
            input: {
              className: styles.input,
              slotProps: {
                root: {
                  className: styleContext({
                    root: true,
                    'date-range-picker': true
                  })
                }
              },
              startAdornment: <CustomDateRangePickerAdornment />,
              endAdornment: (
                <ClearInputAdornment
                  onClear={customProps.onClear}
                  inputValue={componentProps.value}
                  inputIsDisabled={componentProps.disabled}
                />
              )
            }
          }
        }
      }}
    />
  );
}

function dirtyCheck(value: Dayjs | null, initialValue: Dayjs | null) {
  if (value && initialValue) {
    // Determine if the dayjs value is different
    return value.diff(initialValue) !== 0;
  } else if (!value && !initialValue) {
    // Input was null, and is still null
    return false;
  } else {
    // It was null, and now it is not or vice versa
    return true;
  }
}

function asDateRangePicker() {
  return ({ field, form, ...props }: CustomFieldProps) => {
    const { value, initialValue, error } = form.getFieldMeta<[Dayjs, Dayjs] | null>(field.name);

    const { dirty } = form;

    const isDirty = useMemo(() => {
      if (dirty) {
        const initStartRange = initialValue ? initialValue[0] : null;
        const initEndRange = initialValue ? initialValue[1] : null;
        const startRange = value ? value[0] : null;
        const endRange = value ? value[1] : null;

        return dirtyCheck(startRange, initStartRange) || dirtyCheck(endRange, initEndRange);
      }
      return false;
    }, [value, initialValue, dirty]);

    return (
      <DateRangePicker
        name={field.name}
        className={props.className}
        value={field.value as [Dayjs, Dayjs]}
        onChange={(value) => {
          void form.setFieldValue(field.name, value, true);
        }}
        disabled={form.isSubmitting}
        customProps={{
          labelText: props.label ?? '',
          onClear: () => {
            void form.setFieldTouched(field.name, true);
            void form.setFieldValue(field.name, [null, null]);
          },
          isDirty: isDirty,
          isError: Boolean(form.dirty && error),
          errorText: error,
          required: props.required
        }}
      />
    );
  };
}

export const FormikDateRangePicker = asDateRangePicker();

export function CustomDateRangePickerAdornment() {
  const pickerCtx = usePickerContext();

  return (
    <InputAdornment position='start'>
      <IconButton
        onClick={() => {
          pickerCtx.setOpen((state) => !state);
        }}>
        <CalendarMonthIcon fontSize='small' />
      </IconButton>
    </InputAdornment>
  );
}

export function CustomTimePickerAdornment() {
  const pickerCtx = usePickerContext();

  return (
    <InputAdornment position='start'>
      <IconButton
        onClick={() => {
          pickerCtx.setOpen((state) => !state);
        }}>
        <ClockIcon fontSize='small' />
      </IconButton>
    </InputAdornment>
  );
}
